import type { DiagramDocument, DiagramEdge, DiagramNode, DiagramPage } from '../../core/types';
import { removePageWithLinkCleanup } from '../../core/pageImpact';

export type LiveOperation =
  | { type: 'add_node'; pageId: string; next: DiagramNode }
  | { type: 'replace_node'; pageId: string; expected: DiagramNode; next: DiagramNode }
  | { type: 'delete_node'; pageId: string; expected: DiagramNode }
  | { type: 'add_edge'; pageId: string; next: DiagramEdge }
  | { type: 'replace_edge'; pageId: string; expected: DiagramEdge; next: DiagramEdge }
  | { type: 'delete_edge'; pageId: string; expected: DiagramEdge }
  | { type: 'add_page'; next: DiagramPage }
  | { type: 'rename_page'; pageId: string; expectedName: string; nextName: string }
  | { type: 'delete_page'; pageId: string; expected: DiagramPage; fallbackPageId: string };

function same(a: unknown, b: unknown): boolean { return JSON.stringify(a) === JSON.stringify(b); }

function diffPaths(before: unknown, after: unknown, prefix = ''): string[] {
  if (JSON.stringify(before) === JSON.stringify(after)) return [];
  if (!before || !after || typeof before !== 'object' || typeof after !== 'object' || Array.isArray(before) || Array.isArray(after)) return [prefix || '$'];
  const left = before as Record<string, unknown>, right = after as Record<string, unknown>;
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]); const paths: string[] = [];
  for (const key of keys) paths.push(...diffPaths(left[key], right[key], prefix ? `${prefix}.${key}` : key));
  return paths;
}

function getPath(value: unknown, path: string): unknown {
  if (path === '$') return value;
  return path.split('.').reduce<unknown>((current, key) => current && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined, value);
}

function setPath<T>(target: T, path: string, value: unknown): T {
  if (path === '$') return structuredClone(value) as T;
  const clone = structuredClone(target) as Record<string, unknown>; const parts = path.split('.'); let cursor = clone;
  for (let index = 0; index < parts.length - 1; index += 1) { const key = parts[index]; const existing = cursor[key]; if (!existing || typeof existing !== 'object' || Array.isArray(existing)) cursor[key] = {}; cursor = cursor[key] as Record<string, unknown>; }
  const last = parts[parts.length - 1]; if (value === undefined) delete cursor[last]; else cursor[last] = structuredClone(value);
  return clone as T;
}

function mergeDisjoint<T>(expected: T, desired: T, current: T): T | null {
  const localPaths = new Set(diffPaths(expected, current)); const remotePaths = diffPaths(expected, desired);
  if (remotePaths.some((path) => localPaths.has(path) || [...localPaths].some((other) => other.startsWith(`${path}.`) || path.startsWith(`${other}.`)))) return null;
  let merged = structuredClone(current);
  for (const path of remotePaths) merged = setPath(merged, path, getPath(desired, path));
  return merged;
}

/**
 * Gera somente operações de objetos/conexões quando a estrutura de páginas permanece estável.
 * Mudanças de título/página continuam usando a sincronização documental tradicional.
 */
export function deriveLiveOperations(previous: DiagramDocument, next: DiagramDocument, maxOperations = 24): LiveOperation[] | null {
  if (previous.id !== next.id || previous.title !== next.title) return null;
  const nextPages = new Map(next.pages.map((page) => [page.id, page]));
  const operations: LiveOperation[] = [];
  let baseline = structuredClone(previous);
  const deletedPages = previous.pages.filter((page) => !nextPages.has(page.id));
  if (deletedPages.length >= previous.pages.length) return null;
  for (const deleted of deletedPages) {
    const fallbackPageId = next.activePageId && nextPages.has(next.activePageId) ? next.activePageId : next.pages[0]?.id;
    if (!fallbackPageId) return null;
    operations.push({ type: 'delete_page', pageId: deleted.id, expected: deleted, fallbackPageId });
    baseline = removePageWithLinkCleanup(baseline, deleted.id, fallbackPageId);
    if (operations.length > maxOperations) return null;
  }
  const baselinePages = new Map(baseline.pages.map((page) => [page.id, page]));
  for (const afterPage of next.pages) if (!baselinePages.has(afterPage.id)) operations.push({ type: 'add_page', next: afterPage });
  for (const beforePage of baseline.pages) {
    const afterPage = nextPages.get(beforePage.id);
    if (!afterPage) return null;
    if (beforePage.name !== afterPage.name) operations.push({ type: 'rename_page', pageId: beforePage.id, expectedName: beforePage.name, nextName: afterPage.name });
    const beforeNodes = new Map(beforePage.nodes.map((node) => [node.id, node]));
    const afterNodes = new Map(afterPage.nodes.map((node) => [node.id, node]));
    const beforeEdges = new Map(beforePage.edges.map((edge) => [edge.id, edge]));
    const afterEdges = new Map(afterPage.edges.map((edge) => [edge.id, edge]));

    // Excluir conexões antes dos nós mantém a validade intermediária no servidor.
    for (const [id, edge] of beforeEdges) if (!afterEdges.has(id)) operations.push({ type: 'delete_edge', pageId: beforePage.id, expected: edge });
    for (const [id, node] of beforeNodes) if (!afterNodes.has(id)) operations.push({ type: 'delete_node', pageId: beforePage.id, expected: node });
    for (const [id, node] of afterNodes) {
      const before = beforeNodes.get(id);
      if (!before) operations.push({ type: 'add_node', pageId: beforePage.id, next: node });
      else if (!same(before, node)) operations.push({ type: 'replace_node', pageId: beforePage.id, expected: before, next: node });
    }
    for (const [id, edge] of afterEdges) {
      const before = beforeEdges.get(id);
      if (!before) operations.push({ type: 'add_edge', pageId: beforePage.id, next: edge });
      else if (!same(before, edge)) operations.push({ type: 'replace_edge', pageId: beforePage.id, expected: before, next: edge });
    }
    if (operations.length > maxOperations) return null;
  }
  return operations.length > 0 ? operations : null;
}

export interface ApplyLiveOperationsResult {
  document: DiagramDocument;
  applied: number;
  conflicts: Array<{ type: LiveOperation['type']; pageId: string; itemId: string }>;
}

/**
 * Aplica operações recebidas de outro colaborador somente quando o estado local
 * ainda corresponde à precondição enviada ao servidor. Nunca força overwrite.
 */
export function applyRemoteLiveOperations(document: DiagramDocument, operations: LiveOperation[], remoteUpdatedAt?: string): ApplyLiveOperationsResult {
  const next = structuredClone(document);
  const conflicts: ApplyLiveOperationsResult['conflicts'] = [];
  let applied = 0;
  const sameItem = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right);

  for (const op of operations) {
    if (op.type === 'add_page') {
      const existing = next.pages.find((item) => item.id === op.next.id);
      if (!existing) { next.pages.push(structuredClone(op.next)); applied += 1; }
      else if (!sameItem(existing, op.next)) conflicts.push({ type: op.type, pageId: op.next.id, itemId: op.next.id });
      continue;
    }
    if (op.type === 'rename_page') {
      const page = next.pages.find((item) => item.id === op.pageId);
      if (!page) conflicts.push({ type: op.type, pageId: op.pageId, itemId: op.pageId });
      else if (page.name === op.expectedName) { page.name = op.nextName; applied += 1; }
      else if (page.name !== op.nextName) conflicts.push({ type: op.type, pageId: op.pageId, itemId: op.pageId });
      continue;
    }
    if (op.type === 'delete_page') {
      const page = next.pages.find((item) => item.id === op.pageId);
      if (!page) { /* já removida */ }
      else if (next.pages.length <= 1 || !sameItem(page, op.expected)) conflicts.push({ type: op.type, pageId: op.pageId, itemId: op.pageId });
      else {
        const cleaned = removePageWithLinkCleanup(next, op.pageId, op.fallbackPageId);
        next.pages = cleaned.pages; next.activePageId = cleaned.activePageId; applied += 1;
      }
      continue;
    }
    const page = next.pages.find((item) => item.id === op.pageId);
    const itemId = 'next' in op ? op.next.id : op.expected.id;
    if (!page) { conflicts.push({ type: op.type, pageId: op.pageId, itemId }); continue; }
    const nodeAt = (id: string) => page.nodes.findIndex((item) => item.id === id);
    const edgeAt = (id: string) => page.edges.findIndex((item) => item.id === id);

    if (op.type === 'add_node') {
      const at = nodeAt(op.next.id);
      if (at < 0) { page.nodes.push(structuredClone(op.next)); applied += 1; }
      else if (!sameItem(page.nodes[at], op.next)) conflicts.push({ type: op.type, pageId: op.pageId, itemId });
    } else if (op.type === 'replace_node') {
      const at = nodeAt(op.expected.id);
      if (at >= 0 && sameItem(page.nodes[at], op.expected)) { page.nodes[at] = structuredClone(op.next); applied += 1; }
      else if (at >= 0 && sameItem(page.nodes[at], op.next)) { /* já aplicado */ }
      else if (at >= 0) { const merged = mergeDisjoint(op.expected, op.next, page.nodes[at]); if (merged) { page.nodes[at] = merged; applied += 1; } else conflicts.push({ type: op.type, pageId: op.pageId, itemId }); }
      else conflicts.push({ type: op.type, pageId: op.pageId, itemId });
    } else if (op.type === 'delete_node') {
      const at = nodeAt(op.expected.id);
      if (at >= 0 && sameItem(page.nodes[at], op.expected)) { page.nodes.splice(at, 1); applied += 1; }
      else if (at >= 0) conflicts.push({ type: op.type, pageId: op.pageId, itemId });
    } else if (op.type === 'add_edge') {
      const at = edgeAt(op.next.id);
      if (at < 0) { page.edges.push(structuredClone(op.next)); applied += 1; }
      else if (!sameItem(page.edges[at], op.next)) conflicts.push({ type: op.type, pageId: op.pageId, itemId });
    } else if (op.type === 'replace_edge') {
      const at = edgeAt(op.expected.id);
      if (at >= 0 && sameItem(page.edges[at], op.expected)) { page.edges[at] = structuredClone(op.next); applied += 1; }
      else if (at >= 0 && sameItem(page.edges[at], op.next)) { /* já aplicado */ }
      else if (at >= 0) { const merged = mergeDisjoint(op.expected, op.next, page.edges[at]); if (merged) { page.edges[at] = merged; applied += 1; } else conflicts.push({ type: op.type, pageId: op.pageId, itemId }); }
      else conflicts.push({ type: op.type, pageId: op.pageId, itemId });
    } else if (op.type === 'delete_edge') {
      const at = edgeAt(op.expected.id);
      if (at >= 0 && sameItem(page.edges[at], op.expected)) { page.edges.splice(at, 1); applied += 1; }
      else if (at >= 0) conflicts.push({ type: op.type, pageId: op.pageId, itemId });
    }
  }
  if (applied > 0 && remoteUpdatedAt) next.updatedAt = remoteUpdatedAt;
  return { document: next, applied, conflicts };
}

export function isLiveOperationArray(value: unknown): value is LiveOperation[] {
  if (!Array.isArray(value) || value.length < 1 || value.length > 24) return false;
  const allowed = new Set(['add_node','replace_node','delete_node','add_edge','replace_edge','delete_edge','add_page','rename_page','delete_page']);
  return value.every((item) => {
    if (!item || typeof item !== 'object') return false;
    const row = item as Record<string, unknown>;
    if (typeof row.type !== 'string' || !allowed.has(row.type)) return false;
    if (row.type === 'add_page') return Boolean(row.next && typeof row.next === 'object' && typeof (row.next as Record<string, unknown>).id === 'string' && typeof (row.next as Record<string, unknown>).name === 'string');
    if (typeof row.pageId !== 'string' || row.pageId.length > 160) return false;
    if (row.type === 'rename_page') return typeof row.expectedName === 'string' && typeof row.nextName === 'string' && row.expectedName.length <= 160 && row.nextName.length <= 160;
    if (row.type === 'delete_page') return Boolean(row.expected && typeof row.expected === 'object' && (row.expected as Record<string, unknown>).id === row.pageId && typeof row.fallbackPageId === 'string' && row.fallbackPageId.length <= 160);
    if (row.type === 'add_node' || row.type === 'add_edge') return Boolean(row.next && typeof row.next === 'object' && typeof (row.next as Record<string, unknown>).id === 'string');
    if (row.type === 'delete_node' || row.type === 'delete_edge') return Boolean(row.expected && typeof row.expected === 'object' && typeof (row.expected as Record<string, unknown>).id === 'string');
    return Boolean(row.expected && row.next && typeof row.expected === 'object' && typeof row.next === 'object' && (row.expected as Record<string, unknown>).id === (row.next as Record<string, unknown>).id);
  });
}
